import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@ui-kit';
import { ErrorState } from '@ui-kit';
import { EmptyState } from '@ui-kit';
import { useErrorCodes } from '../hooks/useErrorCodes';
import {
  ErrorCodeCatalogTree,
  CatalogSkeleton,
} from '../components/ErrorCodeCatalogTree';
import { BusinessThrowPanel } from '../components/BusinessThrowPanel';
import { ValidationThrowPanel } from '../components/ValidationThrowPanel';
import { AuthThrowPanel } from '../components/AuthThrowPanel';
import { ServerThrowPanel } from '../components/ServerThrowPanel';
import type { ErrorCodeNode } from '../types/errorDemo';

// ─── Keyframe CSS ─────────────────────────────────────────────────────────────

const KEYFRAME_CSS = `
@keyframes spin  { to { transform: rotate(360deg); } }
@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
`;

// ─── Panel routing ────────────────────────────────────────────────────────────

type PanelType = 'business' | 'validation' | 'auth' | 'server' | null;

function getPanelType(node: ErrorCodeNode | null): PanelType {
  if (!node) return null;
  const endpoint = node.throwEndpoint;
  // R8-E-001: match /simulate/* (renamed from /throw/* to bypass Nginx WAF)
  if (endpoint.includes('/simulate/validation') || endpoint.includes('/throw/validation')) return 'validation';
  if (endpoint.includes('/simulate/auth') || endpoint.includes('/throw/auth')) return 'auth';
  if (endpoint.includes('/simulate/server') || endpoint.includes('/throw/server')) return 'server';
  return 'business';
}

// ─── Right panel placeholder ──────────────────────────────────────────────────

function RightPanelPlaceholder() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-12)',
        color: 'var(--color-text-muted)',
        fontSize: 'var(--font-size-sm)',
        gap: 'var(--spacing-3)',
        textAlign: 'center',
        border: '2px dashed var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        minHeight: 240,
      }}
    >
      <span
        aria-hidden="true"
        style={{ fontSize: 'var(--font-size-xl)' }}
      >
        ←
      </span>
      <span>{t('errorDemo.catalog.placeholder')}</span>
    </div>
  );
}

// ─── Common modules panel ─────────────────────────────────────────────────────

interface CommonModuleRow {
  module: string;
  api: string;
  role: string;
  snippet: string;
}

const COMMON_MODULES: CommonModuleRow[] = [
  {
    module: 'eraf-core C001',
    api: 'GlobalExceptionHandler',
    role: '4 throw endpoint 의 응답 본문 자동 생성',
    snippet: 'throw new BusinessException(NOT_FOUND, 42)',
  },
  {
    module: 'eraf-core C001',
    api: 'ApiResponse.success(data)',
    role: 'GET /codes 정상 응답 래핑',
    snippet: 'return ApiResponse.success(catalog);',
  },
  {
    module: 'eraf-core C001',
    api: 'CommonErrorCode.NOT_FOUND 등',
    role: '카탈로그 소스 + throw 시 ErrorCode 식별자',
    snippet: 'throw new NotFoundException(CommonErrorCode.NOT_FOUND, args);',
  },
  {
    module: 'eraf-core C006',
    api: 'RequestContextHolder.current().traceId()',
    role: '결과 카드 traceId 표시',
    snippet: 'String trace = RequestContextHolder.current().traceId();',
  },
  {
    module: 'eraf-core C006',
    api: 'TraceIdFilter (자동)',
    role: 'X-Trace-Id 응답 헤더',
    snippet: '(filter 자동 — 코드 0줄)',
  },
  {
    module: 'eraf-core C014',
    api: '@Audited(action=CUSTOM)',
    role: '4 throw endpoint 호출 감사',
    snippet: '@Audited(customAction="ERROR_DEMO_THROWN") ...',
  },
  {
    module: 'eraf-security C028',
    api: 'JwtAuthenticationFilter',
    role: '5 endpoint 인증 강제',
    snippet: '(filter 자동)',
  },
  {
    module: 'eraf-security C029',
    api: 'SecurityChainCustomizer',
    role: 'URL prefix 인증 등록',
    snippet: '.requestMatchers("/api/eraf/error-demo/**").authenticated()',
  },
  {
    module: 'eraf-swagger C037',
    api: '@ApiErrorResponses({CommonErrorCode.class})',
    role: '의도된 4xx 시나리오 OpenAPI 노출',
    snippet: '@ApiErrorResponses(...) @PostMapping("/simulate/business")',
  },
  {
    module: 'eraf-data-jpa C042',
    api: 'GenericRepository',
    role: '표시만 (호출 0 — 본 biz 신규 entity 0)',
    snippet: '(사용 예 없음 — 학습 카탈로그 노출 목적)',
  },
];

function CommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        marginTop: 'var(--spacing-8)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {/* R7-086: explicit button element — not a div — ensures click/Enter always fires.
           aria-label = visible text so screen readers announce the control name.  */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="error-demo-common-panel"
        aria-label={t(open ? 'errorDemo.commonUsage.collapse' : 'errorDemo.commonUsage.expand') + ' — ' + t('errorDemo.commonUsage.title')}
        data-testid="common-usage-toggle"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>{t('errorDemo.commonUsage.title')}</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="error-demo-common-panel" style={{ overflowX: 'auto' }}>
          <table
            aria-label={t('errorDemo.commonUsage.title')}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-background)',
            }}
          >
            <thead>
              <tr>
                {[t('errorDemo.commonUsage.col.module'), t('errorDemo.commonUsage.col.api'), t('errorDemo.commonUsage.col.role'), t('errorDemo.commonUsage.col.snippet')].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      background: 'var(--color-surface-muted)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_MODULES.map((m, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom:
                        idx < COMMON_MODULES.length - 1
                          ? '1px solid var(--color-surface-border)'
                          : 'none',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom:
                        idx < COMMON_MODULES.length - 1
                          ? '1px solid var(--color-surface-border)'
                          : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--color-brand-primary)',
                        fontSize: 'var(--font-size-xs)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.api}
                    </code>
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom:
                        idx < COMMON_MODULES.length - 1
                          ? '1px solid var(--color-surface-border)'
                          : 'none',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {m.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom:
                        idx < COMMON_MODULES.length - 1
                          ? '1px solid var(--color-surface-border)'
                          : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-surface-elevated)',
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'inline-block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.snippet}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── ErrorDemoPage ────────────────────────────────────────────────────────────

/**
 * /error-demo — split layout
 * Left: ErrorCode catalog tree (4 groups)
 * Right: Throw panel (business / validation / auth / server)
 */
export function ErrorDemoPage() {
  const { t } = useTranslation();
  const { data: catalog, isLoading, isError, error, refetch } = useErrorCodes();

  const [selectedNode, setSelectedNode] = useState<ErrorCodeNode | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(['COMMON', 'AUTH', 'VALIDATION', 'USER'])
  );

  const handleToggleGroup = (prefix: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) {
        next.delete(prefix);
      } else {
        next.add(prefix);
      }
      return next;
    });
  };

  const handleSelectNode = (node: ErrorCodeNode) => {
    setSelectedNode(node);
  };

  const panelType = getPanelType(selectedNode);

  const isEmpty =
    !isLoading &&
    !isError &&
    catalog &&
    catalog.groups.every((g) => g.nodes.length === 0);

  return (
    <>
      <style>{KEYFRAME_CSS}</style>

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: 'var(--spacing-6)',
        }}
      >
        {/* Page header */}
        <PageHeader
          title={t('errorDemo.page.title')}
          breadcrumb={
            <span>
              {t('errorDemo.page.title')}
            </span>
          }
        />

        {/* Description */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginTop: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          {t('errorDemo.page.description')}
        </p>

        {/* Error state — catalog load failure */}
        {isError && (
          <ErrorState
            message={
              (error as { message?: string })?.message ?? t('errorDemo.error.loadFailed')
            }
            onRetry={() => refetch()}
          />
        )}

        {/* Empty state — catalog is empty (defensive) */}
        {isEmpty && (
          <EmptyState
            title={t('errorDemo.catalog.empty')}
            description={t('errorDemo.catalog.emptyDescription')}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('errorDemo.catalog.reload')}
              </button>
            }
          />
        )}

        {/* Main split layout */}
        {!isError && !isEmpty && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'clamp(220px, 28%, 300px) 1fr',
              gap: 'var(--spacing-6)',
              alignItems: 'start',
            }}
          >
            {/* Left — catalog tree */}
            <div style={{ minHeight: 400 }}>
              {isLoading ? (
                <CatalogSkeleton />
              ) : catalog ? (
                <ErrorCodeCatalogTree
                  catalog={catalog}
                  selectedCode={selectedNode?.code ?? null}
                  expandedGroups={expandedGroups}
                  onToggleGroup={handleToggleGroup}
                  onSelectNode={handleSelectNode}
                />
              ) : null}
            </div>

            {/* Right — throw panel */}
            <div>
              {!selectedNode ? (
                <RightPanelPlaceholder />
              ) : panelType === 'validation' ? (
                <ValidationThrowPanel />
              ) : panelType === 'auth' ? (
                <AuthThrowPanel selectedNode={selectedNode} />
              ) : panelType === 'server' ? (
                <ServerThrowPanel selectedNode={selectedNode} />
              ) : (
                <BusinessThrowPanel selectedNode={selectedNode} />
              )}
            </div>
          </div>
        )}

        {/* Bottom — common modules panel */}
        <CommonUsagePanel />
      </div>
    </>
  );
}
